import React from 'react'

type Segment = { type: 'text' | 'strong' | 'em' | 'code'; value: string }

const parseInline = (line: string): Segment[] => {
    const segments: Segment[] = []
    let rest = line

    const pushText = (value: string) => {
        if (value) segments.push({ type: 'text', value })
    }

    const findNext = (s: string) => {
        const codeIndex = s.indexOf('`')
        const strongIndex = s.indexOf('**')
        const starEmIndex = s.indexOf('*')
        const underscoreEmIndex = s.indexOf('_')

        const candidates = [
            { type: 'code' as const, index: codeIndex },
            { type: 'strong' as const, index: strongIndex },
            { type: 'emStar' as const, index: starEmIndex },
            { type: 'emUnderscore' as const, index: underscoreEmIndex },
        ].filter((c) => c.index !== -1)

        if (!candidates.length) return null

        candidates.sort((a, b) => a.index - b.index)
        return candidates[0]
    }

    while (rest.length) {
        const next = findNext(rest)
        if (!next) {
            pushText(rest)
            break
        }

        pushText(rest.slice(0, next.index))
        rest = rest.slice(next.index)

        if (next.type === 'strong' && rest.startsWith('**')) {
            const end = rest.indexOf('**', 2)
            if (end === -1) {
                pushText(rest)
                break
            }
            segments.push({ type: 'strong', value: rest.slice(2, end) })
            rest = rest.slice(end + 2)
            continue
        }

        if (next.type === 'code' && rest.startsWith('`')) {
            const end = rest.indexOf('`', 1)
            if (end === -1) {
                pushText(rest)
                break
            }
            segments.push({ type: 'code', value: rest.slice(1, end) })
            rest = rest.slice(end + 1)
            continue
        }

        if ((next.type === 'emStar' && rest.startsWith('*')) || (next.type === 'emUnderscore' && rest.startsWith('_'))) {
            // Avoid treating "**" as italic start
            if (rest.startsWith('**')) {
                pushText('*')
                rest = rest.slice(1)
                continue
            }
            const marker = rest[0]
            const end = rest.indexOf(marker, 1)
            if (end === -1) {
                pushText(rest)
                break
            }
            segments.push({ type: 'em', value: rest.slice(1, end) })
            rest = rest.slice(end + 1)
            continue
        }
    }

    return segments
}

export const renderMarkdown = (markdown: string) => {
    const lines = markdown.split(/\r?\n/)
    const blocks: React.ReactNode[] = []

    let index = 0
    while (index < lines.length) {
        const rawLine = lines[index]
        const line = rawLine ?? ''
        const trimmed = line.trim()

        // fenced code blocks
        if (trimmed.startsWith('```')) {
            const lang = trimmed.slice(3).trim()
            const codeLines: string[] = []
            index += 1
            while (index < lines.length && !lines[index].trim().startsWith('```')) {
                codeLines.push(lines[index])
                index += 1
            }
            // skip closing fence if present
            if (index < lines.length && lines[index].trim().startsWith('```')) {
                index += 1
            }
            const code = codeLines.join('\n')
            blocks.push(
                <pre key={`code-${blocks.length}`} style={{ overflow: 'auto', padding: 12, borderRadius: 12, border: '1px solid var(--border)' }}>
                    <code className={lang ? `language-${lang}` : undefined}>{code}</code>
                </pre>
            )
            continue
        }

        // horizontal rule
        if (/^(\*{3,}|-{3,}|_{3,})\s*$/.test(trimmed)) {
            blocks.push(<hr key={`hr-${blocks.length}`} style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '12px 0' }} />)
            index += 1
            continue
        }

        // blank line
        if (!trimmed) {
            blocks.push(<div key={`sp-${blocks.length}`} style={{ height: 12 }} />)
            index += 1
            continue
        }

        // headings up to ######
        const headingMatch = /^(#{1,6})\s+(.*)$/.exec(trimmed)
        if (headingMatch) {
            const level = headingMatch[1].length
            const text = headingMatch[2]
            const key = `h-${blocks.length}`
            if (level === 1) blocks.push(<h1 key={key}>{text}</h1>)
            else if (level === 2) blocks.push(<h2 key={key}>{text}</h2>)
            else if (level === 3) blocks.push(<h3 key={key}>{text}</h3>)
            else if (level === 4) blocks.push(<h4 key={key}>{text}</h4>)
            else if (level === 5) blocks.push(<h5 key={key}>{text}</h5>)
            else blocks.push(<h6 key={key}>{text}</h6>)
            index += 1
            continue
        }

        // blockquote (single line)
        if (trimmed.startsWith('> ')) {
            const segments = parseInline(trimmed.slice(2))
            blocks.push(
                <blockquote
                    key={`bq-${blocks.length}`}
                    style={{ margin: '0 0 10px', padding: '8px 12px', borderLeft: '3px solid var(--border)', color: 'var(--text-muted)' }}
                >
                    {segments.map((seg, i) =>
                        seg.type === 'strong' ? (
                            <strong key={i}>{seg.value}</strong>
                        ) : seg.type === 'em' ? (
                            <em key={i}>{seg.value}</em>
                        ) : seg.type === 'code' ? (
                            <code key={i}>{seg.value}</code>
                        ) : (
                            <span key={i}>{seg.value}</span>
                        )
                    )}
                </blockquote>
            )
            index += 1
            continue
        }

        // unordered list block
        if (/^[-*]\s+/.test(trimmed)) {
            const items: string[] = []
            while (index < lines.length && /^[-*]\s+/.test(lines[index].trim())) {
                items.push(lines[index].trim().replace(/^[-*]\s+/, ''))
                index += 1
            }
            blocks.push(
                <ul key={`ul-${blocks.length}`} style={{ margin: '8px 0 10px', paddingLeft: 18 }}>
                    {items.map((it, ii) => {
                        const segments = parseInline(it)
                        return (
                            <li key={ii}>
                                {segments.map((seg, i) =>
                                    seg.type === 'strong' ? (
                                        <strong key={i}>{seg.value}</strong>
                                    ) : seg.type === 'em' ? (
                                        <em key={i}>{seg.value}</em>
                                    ) : seg.type === 'code' ? (
                                        <code key={i}>{seg.value}</code>
                                    ) : (
                                        <span key={i}>{seg.value}</span>
                                    )
                                )}
                            </li>
                        )
                    })}
                </ul>
            )
            continue
        }

        // ordered list block
        if (/^\d+\.\s+/.test(trimmed)) {
            const items: string[] = []
            while (index < lines.length && /^\d+\.\s+/.test(lines[index].trim())) {
                items.push(lines[index].trim().replace(/^\d+\.\s+/, ''))
                index += 1
            }
            blocks.push(
                <ol key={`ol-${blocks.length}`} style={{ margin: '8px 0 10px', paddingLeft: 18 }}>
                    {items.map((it, ii) => {
                        const segments = parseInline(it)
                        return (
                            <li key={ii}>
                                {segments.map((seg, i) =>
                                    seg.type === 'strong' ? (
                                        <strong key={i}>{seg.value}</strong>
                                    ) : seg.type === 'em' ? (
                                        <em key={i}>{seg.value}</em>
                                    ) : seg.type === 'code' ? (
                                        <code key={i}>{seg.value}</code>
                                    ) : (
                                        <span key={i}>{seg.value}</span>
                                    )
                                )}
                            </li>
                        )
                    })}
                </ol>
            )
            continue
        }

        // paragraph
        const segments = parseInline(line)
        blocks.push(
            <p key={`p-${blocks.length}`} style={{ whiteSpace: 'pre-wrap', margin: '0 0 10px' }}>
                {segments.map((seg, i) =>
                    seg.type === 'strong' ? (
                        <strong key={i}>{seg.value}</strong>
                    ) : seg.type === 'em' ? (
                        <em key={i}>{seg.value}</em>
                    ) : seg.type === 'code' ? (
                        <code key={i}>{seg.value}</code>
                    ) : (
                        <span key={i}>{seg.value}</span>
                    )
                )}
            </p>
        )
        index += 1
    }

    return <div>{blocks}</div>
}
